import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ApolloProvider, useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import client from '../../../../lib/apolloClient';
import { get } from 'lodash';
import NotificationTrigger from '../components/NotificationTrigger';
import NotificationDrawer from '../components/NotificationDrawer';

// Reuse the same GraphQL query from the original container
export const getCurrentUser = gql`
  query getCurrentUser($after: String) {
    getCurrentUser {
      user_name
      open_notifications_count
      notifications(first: 10, after: $after) {
        edges {
          node {
            id
            action
            notifiableType
            createdAt
            readAt
            data
            notifiable {
              __typename
              ... on BusinessContinuityPlan {
                __typename
                id
              }
              ... on PersonalMessage {
                id
                __typename
                conversation {
                  id
                  conversationType
                }
              }
              ... on EducationalContent {
                __typename
                id
                title
              }
              ... on PartialSaleListingUser {
                __typename
                id
                partial_sale_listing {
                  id
                  segment_details
                }
              }
              ... on VectorDocument {
                __typename
                id
                title
              }
            }
            actor {
              allowed_name
              user_name
              profile_picture
            }
            recipient {
              user_name
            }
          }
          cursor
        }
        pageInfo {
          hasPreviousPage
          hasNextPage
          endCursor
          startCursor
        }
      }
    }
  }
`;

const SuccessionNotificationsContainerInner = ({ isOpen, onToggle, onClose }) => {
  const [notifications, setNotifications] = useState([]);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [endCursor, setEndCursor] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const { loading, error, data, refetch, fetchMore } = useQuery(getCurrentUser, {
    variables: { after: null },
    fetchPolicy: 'no-cache', // Completely bypass Apollo cache to avoid conflicts with different Apollo versions
    onCompleted: (data) => {
      if (data && data.getCurrentUser && data.getCurrentUser.notifications) {
        const edges = data.getCurrentUser.notifications.edges || [];
        setNotifications(edges.map(edge => edge.node));
        setHasNextPage(data.getCurrentUser.notifications.pageInfo?.hasNextPage || false);
        setEndCursor(data.getCurrentUser.notifications.pageInfo?.endCursor || null);
      }
    }
  });

  const handleLoadMore = async () => {
    if (!hasNextPage || isLoadingMore) return;
    
    setIsLoadingMore(true);
    try {
      const result = await fetchMore({
        variables: { after: endCursor },
        fetchPolicy: 'no-cache', // Consistent with main query
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;
          
          const newEdges = fetchMoreResult.getCurrentUser.notifications.edges;
          const newNotifications = newEdges.map(edge => edge.node);
          
          setNotifications(prevNotifications => [...prevNotifications, ...newNotifications]);
          setHasNextPage(fetchMoreResult.getCurrentUser.notifications.pageInfo?.hasNextPage || false);
          setEndCursor(fetchMoreResult.getCurrentUser.notifications.pageInfo?.endCursor || null);
          
          return {
            ...prev,
            getCurrentUser: {
              ...prev.getCurrentUser,
              notifications: {
                ...fetchMoreResult.getCurrentUser.notifications,
                edges: [...prev.getCurrentUser.notifications.edges, ...newEdges]
              }
            }
          };
        }
      });
    } catch (error) {
      console.error('Error loading more notifications:', error);
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      // Optimistically update the notification as read
      setNotifications(prevNotifications =>
        prevNotifications.map(notification =>
          notification.id === notificationId
            ? { ...notification, readAt: new Date().toISOString() }
            : notification
        )
      );
      
      // Refetch to sync with server
      await refetch();
    } catch (error) {
      console.error('Error marking notification as read:', error);
      // Revert optimistic update on error
      await refetch();
    }
  };

  if (loading) {
    return (
      <NotificationTrigger
        openCount={0}
        onOpen={onToggle}
        isLoading={true}
      />
    );
  }

  if (error) {
    console.error('Error loading notifications:', error);
    return (
      <NotificationTrigger
        openCount={0}
        onOpen={onToggle}
        hasError={true}
      />
    );
  }

  const user = data?.getCurrentUser;
  const notificationCount = user?.open_notifications_count || 0;

  return (
    <>
      <NotificationTrigger
        openCount={notificationCount}
        onOpen={onToggle}
      />
      <NotificationDrawer
        isOpen={isOpen}
        onClose={onClose}
        notifications={notifications}
        openCount={notificationCount}
        updateCount={(countChange) => {
          // Update the count when notifications are marked as read
          // This would typically refetch the query or update local state
          refetch();
        }}
        after={endCursor}
        hasNextPage={hasNextPage}
        fetchMore={handleLoadMore}
        resetLoaded={(newState) => {
          setNotifications(newState.notifications);
          setHasNextPage(newState.hasNextPage);
          setEndCursor(newState.after);
        }}
        loading={loading}
        error={error}
      />
    </>
  );
};

const SuccessionNotificationsContainer = ({ isOpen: externalIsOpen, onToggle: externalOnToggle, onClose: externalOnClose }) => {
  // Use internal state if no external state management is provided
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  
  // Determine if we're using external or internal state management
  const isExternallyControlled = externalIsOpen !== undefined && externalOnToggle !== undefined;
  
  // Use external state if provided, otherwise use internal state
  const isOpen = isExternallyControlled ? externalIsOpen : internalIsOpen;
  const onToggle = isExternallyControlled ? externalOnToggle : () => {
    setInternalIsOpen(!internalIsOpen);
  };
  const onClose = isExternallyControlled ? externalOnClose : () => {
    setInternalIsOpen(false);
  };
  
  return (
    <ApolloProvider client={client}>
      <SuccessionNotificationsContainerInner
        isOpen={isOpen}
        onToggle={onToggle}
        onClose={onClose}
      />
    </ApolloProvider>
  );
};

SuccessionNotificationsContainer.propTypes = {
  isOpen: PropTypes.bool,
  onToggle: PropTypes.func,
  onClose: PropTypes.func,
  inMenu: PropTypes.bool,
  color: PropTypes.string,
  features: PropTypes.arrayOf(PropTypes.string),
  theme_name: PropTypes.string,
};

SuccessionNotificationsContainer.defaultProps = {
  isOpen: undefined,
  onToggle: undefined,
  onClose: undefined,
  inMenu: false,
  color: 'primary',
  features: [],
  theme_name: 'default',
};

export default SuccessionNotificationsContainer; 